/* OsmOptionsRenderer.js */

"use strict";
const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
const SingleCombobox = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single
    .ComboBox;
const Forms = require("@gluenetworks/forms");
const FormsComponent = Forms.ui.component;
const _ = require("lodash");

class OsmOptionsRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("osm-options-gl");
        this._rootElem.component = this._model;
        this._logger = this._model.logger;

        this._titleText = document.createElement("h3");
        this._titleText.innerHTML = "Options";

        this._buttonBarElem = document.createElement("button-container");
        this._buttonBarElem.classList.add("button-bar");

        this._cancelButton = new ButtonControl({
            label: "Cancel",
            darkTheme: true,
        });
        this._saveButton = new ButtonControl({
            label: "Save",
            darkTheme: true,
            active: true,
            disabled: this._model._isReadOnly,
        });

        this._vendorDropdown = new SingleCombobox({
            darkTheme: true,
            autoSelect: false,
        });
        this._osDropdown = new SingleCombobox({
            darkTheme: true,
            autoSelect: false,
        });
        this._osmOptionFormContainer = document.createElement("div");
        this._osmOptionFormContainer.classList.add("osm-option-form");
        this._formsComponent = new FormsComponent();

        this._onOsDropdownChange = this._onOsDropdownChange.bind(this);
        this._onVendorDropdownChange = this._onVendorDropdownChange.bind(this);
        this._onFormsPendingPatchesChanged = _.debounce(
            this._onFormsPendingPatchesChanged.bind(this),
            100
        );

        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._cancelButton.on("UserClick", this._cancelAction.bind(this));
        this._saveButton.on("UserClick", (this._saveAction = this._saveAction.bind(this)));
        this._vendorDropdown.on("UserValue", this._onVendorDropdownChange);
        this._osDropdown.on("UserValue", this._onOsDropdownChange);
        this._formsComponent.on("PendingPatchesChanged", this._onFormsPendingPatchesChanged);
    }

    _unregisterListeners() {
        this._cancelButton.removeListener("UserClick", this._cancelAction);
        this._saveButton.removeListener("UserClick", this._saveAction);
        this._vendorDropdown.removeAllListeners("UserValue");
        this._osDropdown.removeAllListeners("UserValue");
        this._formsComponent.removeAllListeners("PendingPatchesChanged");
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();

        let promArr = [
            this._cancelButton.shutdown(),
            this._saveButton.shutdown(),
            this._vendorDropdown.shutdown(),
            this._osDropdown.shutdown(),
            this._formsComponent.shutdown(),
        ];

        return Promise.all(promArr).then(
            () => {
                this._titleText.remove();
                this._titleText = undefined;
                this._buttonBarElem.remove();
                this._cancelButton = undefined;
                this._saveButton = undefined;
                this._vendorDropdown = undefined;
                this._osDropdown = undefined;
                this._formsComponent = undefined;
                this._osmOptionFormContainer.remove();
                this._osmOptionFormContainer = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "OsmOptionsRenderer.shutdown() error.");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return new Promise((resolve) => {
            if (!this._rootElem.hasChildNodes()) {
                this._rootElem.appendChild(this._titleText);
                const vendorLabelEl = document.createElement("label");
                vendorLabelEl.innerHTML = "Vendor";
                this._rootElem.appendChild(vendorLabelEl);
                this._rootElem.appendChild(this._vendorDropdown.renderer.getRootElement());
                const OsLabelEl = document.createElement("label");
                OsLabelEl.innerHTML = "OS";
                this._rootElem.appendChild(OsLabelEl);
                this._rootElem.appendChild(this._osDropdown.renderer.getRootElement());
                if (this._model._isReadOnly) {
                    this._osmOptionFormContainer.classList.add("disabled-form");
                }
                this._rootElem.appendChild(this._osmOptionFormContainer);
                this._buttonBarElem.appendChild(this._cancelButton.renderer.rootElement);
                this._buttonBarElem.appendChild(this._saveButton.renderer.rootElement);
                this._rootElem.appendChild(this._buttonBarElem);

                this._vendorDropdown.setOptions(this._model.generateOsmOptionsVendorDDOpts());
                this._osDropdown.setOptions(this._model.generateOsmOptionsOsDDOpts("*"));
                this._vendorDropdown.setValue("*", true);
                this._onVendorDropdownChange("*");
                this._osmOptionFormContainer.appendChild(this._formsComponent.renderer.rootElement);
            }
            resolve();
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/
    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _cancelAction() {
        this._model._plan.setOsmOptions(_.get(this._model._plan, `_pristineProperties.osmOptions`));
        this._model.cancelAction();
    }

    _saveAction() {
        this._model.saveAction();
    }

    _onVendorDropdownChange(val) {
        const osOptions = this._model.generateOsmOptionsOsDDOpts(val);
        this._osDropdown.setOptions(osOptions);
        this._osDropdown.setValue(osOptions[0].value);
        this._onOsDropdownChange(osOptions[0].value);
    }
    _onOsDropdownChange() {
        const jtype = this._getOptsJtype(
            this._vendorDropdown.getValue(),
            this._osDropdown.getValue()
        );
        // const info = jtype["@Info"];
        const wrapJtype = {};
        const vendor = this._vendorDropdown
            .getOptions()
            .find((obj) => obj.value === this._vendorDropdown.getValue());
        const os = this._osDropdown
            .getOptions()
            .find((obj) => obj.value === this._osDropdown.getValue());
        let wrapJtypeLabel = "Options";
        if (vendor && vendor.value === "*" && os && os.value === "*") {
            wrapJtypeLabel = "Common Options";
        } else {
            const vendorLabel = vendor ? vendor.label : "";
            const osLabel = os ? os.label : "";
            wrapJtypeLabel = `${vendorLabel} ${osLabel} Options`.trim();
        }
        Object.assign(
            wrapJtype,
            {
                "@Display.layout": {},
            },
            {
                "@Display.styles": {
                    cssClasses: ["dark"],
                },
            }
        );
        wrapJtype[wrapJtypeLabel] = jtype;
        this._osmOptionJtypeKey = wrapJtypeLabel;
        this._formsComponent.setTypeInformation(wrapJtype, true);
        const formPristineVal = this._getFormPristineValue();
        if (formPristineVal) {
            this._formsComponent.setPristineValue(formPristineVal, true);
        }
        return this._formsComponent.renderer.render();
    }

    _onFormsPendingPatchesChanged() {
        if (this._model._isReadOnly) {
            return;
        }
        const formVals = _.get(this._formsComponent.getValue(), this._osmOptionJtypeKey, {});
        const vendor = this._vendorDropdown.getValue();
        const os = this._osDropdown.getValue();
        let osmOptions = _.get(this._model._plan, "osmOptions", {});
        if (vendor === "*" && os === "*") {
            osmOptions = _.set(osmOptions, `commonOptions`, formVals);
        } else {
            osmOptions = _.set(osmOptions, `vendorOsOptions.${vendor}.${os}.options`, formVals);
        }
        this._model._plan.setOsmOptions(osmOptions);
    }

    _getFormPristineValue() {
        const vendor = this._vendorDropdown.getValue();
        const os = this._osDropdown.getValue();
        let formPristineVal;
        if (vendor === "*" && os === "*") {
            formPristineVal = _.get(
                this._model._plan,
                `properties.osmOptions.commonOptions`,
                undefined
            );
        } else {
            formPristineVal = _.get(
                this._model._plan,
                `properties.osmOptions.vendorOsOptions.${vendor}.${os}.options`,
                undefined
            );
        }
        // Merging from execution entity for now
        if (this._model._executionEntity) {
            const execOptions = _.get(
                this._model._executionEntity,
                `properties.osmOptions.vendorOsOptions.${vendor}.${os}.options`,
                {}
            );
            formPristineVal = _.defaultsDeep({}, execOptions, formPristineVal);
        }

        let formVal = {};
        formVal[this._osmOptionJtypeKey] = formPristineVal;
        return formVal;
    }

    _getOptsJtype(vendor, os) {
        return this._model._osmOptions[vendor][os];
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = OsmOptionsRenderer;
